import {
	actions,
	persistor,
	store,
	slices,
	selectors,
	userAPI,
} from "./redux/store";
import { GoogleSigninResponse } from "./redux/services/api/types";

import "./components";
import { AuthCookies } from "./utils/auth-cookies";
import {
	SessionController,
	SessionControllerConfiguration,
	SessionEvents,
	SessionSnapshot,
} from "../session-controller";
import { EnvironmentType, getEnvironmentType } from "../utils/environment";
import { UserAccountEvents } from "./types";
import { isProtectedRoute } from "./utils/routes";
import { setupLoginModalListeners } from "./utils/login-modal-setup";
import { sendEventLoggedUser, sendEventLogin, sendEventRegister, sendEventUserLoggedOut } from "./utils/analytics";

const SESSION_PING_TIME_INTERVAL = 360000; // 6 minutes
const SESSION_CONTROLLER_SETTINGS: SessionControllerConfiguration = {
	idleTimeBetweenSessions: 900000, // 15 minutes
	timeBetweenUpdates: 15000, // 15 seconds
	debug:
		getEnvironmentType() === EnvironmentType.DEVELOPMENT ||
		getEnvironmentType() === EnvironmentType.STAGING,
};
const TIME_TO_UPDATE_NOTIFICATIONS_SINCE_LAST_UPDATE = 300000; // 5 minutes


setupLoginModalListeners();

const isNewUser = sessionStorage.getItem('isNewUser');
if (isNewUser) {
    sessionStorage.removeItem('isNewUser');
    if (
        !window.location.pathname.includes('checkout') &&
        !document.querySelector('welcome-modal')
    ) {
        document.body.appendChild(document.createElement('welcome-modal'));
    }
}


window.addEventListener(UserAccountEvents.LOGGED, async (event) => {
	const { uuid } = (event as any).detail;
	const state = store.getState();
	const authState = state[slices.auth.name];
	const authData = selectors.auth.credentials(authState);
	const isNewUser = selectors.auth.isNewUser(authState);

	try {
		if (isNewUser) {
			sessionStorage.setItem('isNewUser', 'true');
		}
	} catch (error) {
		console.error('Error setting isNewUser', error);
	}

	const response = await store.dispatch(
		userAPI.endpoints.validateToken.initiate(
			authData
		)
	);
	window.location.reload();

	// const pingSession = (snapshot: SessionSnapshot) => {
	//
	//
	//
	// 	// store
	// 	// 	.dispatch(
	// 	// 		userAPI.endpoints.pingSession.initiate({
	// 	// 			...authData,
	// 	// 			data: {
	// 	// 				initialTimeStamp: snapshot.initialTimeStamp,
	// 	// 				lastUpdatedTimeStamp: snapshot.lastUpdatedTimeStamp,
	// 	// 				timeSpent: snapshot.totalTimeSpent,
	// 	// 				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
	// 	// 				currentPage: window.location.pathname,
	// 	// 			},
	// 	// 		})
	// 	// 	)
	// 	// 	.then(() => {
	// 	// 		SessionController.saveSnapshot("pinged", snapshot);
	// 	// 		// store.dispatch(
	// 	// 		// 	userAPI.endpoints.getPendingNotifications.initiate(authData)
	// 	// 		// );
	// 	// 	});
	// };

	let lastPingedSessionSnapshot: SessionSnapshot | null = null;
	SessionController.instance.addEventListener(SessionEvents.START, () => {
		const state = store.getState();
		const authState = state[slices.auth.name];
		// const notificationsState = state[slices.notification.name];
		const authData = selectors.auth.credentials(authState);
	});

	SessionController.create({
		...SESSION_CONTROLLER_SETTINGS,
		userId: uuid,
	});
});

window.addEventListener(UserAccountEvents.LOGOUT, async (event) => {
	const { isPremium, uuid } = (event as any).detail;
	SessionController.delete();
	store.dispatch(actions.auth.logout());
	// store.dispatch(actions.notification.deleteAllNotifications());

	await handleHardReload('/')
});
async function handleHardReload(url: string) {
	await fetch(url, {
		headers: {
			Pragma: 'no-cache',
			Expires: '-1',
			'Cache-Control': 'no-cache',
		},
	});
	window.location.href = url;
	// This is to ensure reload with url's having '#'
	//window.location.reload();
}

window.userLogoutHandler = function () {

	const state = store.getState();
	const authState = state[slices.auth.name];
	const uuid = selectors.auth.getUser(authState)!.uuid;
	const isPremium = selectors.auth.isPremium(authState);

	sendEventUserLoggedOut({ uuid, isPremium });
	window.dispatchEvent(
		new CustomEvent(
			UserAccountEvents.LOGOUT,
			{ detail: { uuid, isPremium } }
		)
	);

};

window.googleLoginHandler = async function (response: GoogleSigninResponse) {
	setupLoginHandler();
	store.dispatch(userAPI.endpoints.googleLogin.initiate(response));
};

window.questionnaireHandler = async function (data: { questionnaireId: number; answers: { questionId: string; answer: number; }[]; action: string }) {
	try {
		store.dispatch(
			userAPI.endpoints.saveQuestionnaire.initiate({
				questionnaireId: data.questionnaireId,
				answers: JSON.stringify(data.answers),
				action: data.action
			})
		)
	} catch (error) {
		console.error('Error saving questionnaire answers:', error);
	}
};

function getLoginReferrer() {
	const params = new URLSearchParams(window.location.search);
	const nextUrl = params.get("next");
	const documentReferrer =
		document.referrer &&
		document.referrer.startsWith(window.location.origin)
			? document.referrer.replace(window.location.origin, "")
			: "";

	let referrer = nextUrl ? nextUrl : documentReferrer;

	if (referrer && !referrer.startsWith("/")) {
		referrer = "";
	}

	if (referrer && !referrer.endsWith("/")) {
		referrer += "/";
	}

	if (referrer) {
		if (referrer.includes("?")) {
			referrer += `&cb=${Date.now()}`;
		} else {
			referrer += `?cb=${Date.now()}`;
		}
	}

	return referrer;
}

function setupLoginHandler() {
	const unsubscribe = store.subscribe(async () => {
		const state = store.getState();
		if (selectors.auth.isLogged(state[slices.auth.name])) {
			unsubscribe();
			const authState = state[slices.auth.name];
			const uuid = selectors.auth.getUser(authState)!.uuid;
			const isNewUser = selectors.auth.isNewUser(authState);
			const isPremium = selectors.auth.isPremium(authState);

			sendEventLogin();
			sendEventLoggedUser({ uuid, isPremium });

			if (isNewUser) sendEventRegister();

			const params = new URLSearchParams(window.location.search);
			// if (params.get("f")) {
			// 	const favoriteId = params.get("f")!;
			// 	const response = await store.dispatch(
			// 		userAPI.endpoints.updateFavorite.initiate({
			// 			...selectors.auth.credentials(state[slices.auth.name]),
			// 			content_id: favoriteId,
			// 		})
			// 	);
			//
			// 	if ("data" in response) {
			// 		sendEventAddFavorite(favoriteId);
			// 	}
			// }

			if (window.location.pathname.includes("checkout")) {
				return window.location.replace(
					`${window.location.origin}/checkout/?step=payment-data`
				);
			}

			// if (
			// 	document
			// 		.getElementById("tmplus-checkout-login")
			// 		?.classList.contains("show")
			// ) {
			// 	return window.location.assign(
			// 		`${window.location.origin}/checkout/?step=payment-data`
			// 	);
			// }

			if (isNewUser) {
				const referrer = getLoginReferrer();
				const nextParam = referrer
					? `?${new URLSearchParams({ next: referrer })}`
					: '';
				return window.location.replace(
					`${window.location.origin}/user/register/${nextParam}`
				);
			}

			if (window.location.pathname.includes("login")) {
				const referrer = getLoginReferrer();
				if (referrer) {
					return window.location.replace(referrer);
				}

				return window.location.replace(
					`${window.location.origin}/meu-perfil/`
				);
			}

			if (selectors.auth.isPremium(authState)) {
				return window.location.reload();
			}
		}
	});
}

// Add function to check if should show questions modal
function checkAndShowQuestionsModal(session: any, selector: string) {
	const timeSinceLogin = Date.now() - session.lastUpdatedTimeStamp;

	if (timeSinceLogin >= 30000) { // 30 seconds
		const questionnaire = document.querySelector(selector) as any;
		if (questionnaire) {
			questionnaire.show();
		}
	}
}

// Check on page load
window.addEventListener('load', () => {
	const session = SessionController.instance.storageSession;
	if (!session) {
		return;
	}

	if(window.canShowQuestionnaire) {
		checkAndShowQuestionsModal(session, 'questions-modal');
	}
});

async function initialAuthValidation() {
	const state = store.getState();
	const authState = state[slices.auth.name];
	const isLogged = selectors.auth.isLogged(authState);
	const isLoggedUserCredentialsValid = AuthCookies.validate(isLogged);

	if (!isLoggedUserCredentialsValid) {
		store.dispatch(slices.auth.actions.logout());
	}


	if (isLogged && isLoggedUserCredentialsValid) {
		const response = await store.dispatch(
			userAPI.endpoints.validateToken.initiate(
				selectors.auth.credentials(authState)
			)
		);

		if ('error' in response && (response.error as any).status !== 'FETCH_ERROR') {
			return store.dispatch(slices.auth.actions.logout());
		}

		const { uuid } = selectors.auth.credentials(authState);
		// sendEventLoggedUser({ uuid });
	}
}

const { bootstrapped } = persistor.getState();
if (!bootstrapped) {
	const unsubscribe = persistor.subscribe(() => {
		const { bootstrapped } = persistor.getState();
		if (bootstrapped) {
			unsubscribe?.call(this);
			initialAuthValidation().then();
		}
	});
}

if (bootstrapped) {
	initialAuthValidation().then();
}
